/**
 * Client HTTP para endpoints Memed do backend Dr. House (T7).
 */

import { apiClient, ApiError, type ApiResponse } from '@/services/apiClient';
import { classifyMemedApiFailure, MemedFrontendError } from './memed.errors';
import type { MemedSession, MemedStatus } from './memed.types';

const SESSION_PATH = '/memed/session';
const STATUS_PATH = '/memed/status';

function extractCode(payload: unknown): string | undefined {
  if (!payload || typeof payload !== 'object') return undefined;
  const obj = payload as Record<string, unknown>;
  if (typeof obj.code === 'string') return obj.code;
  const nested = obj.message;
  if (nested && typeof nested === 'object' && typeof (nested as { code?: unknown }).code === 'string') {
    return (nested as { code: string }).code;
  }
  return undefined;
}

function extractMissingFields(payload: unknown): string[] | undefined {
  if (!payload || typeof payload !== 'object') return undefined;
  const fields = (payload as { missingFields?: unknown }).missingFields;
  if (!Array.isArray(fields)) return undefined;
  return fields.filter((f): f is string => typeof f === 'string');
}

function toMemedError(error: unknown): MemedFrontendError {
  if (error instanceof MemedFrontendError) return error;

  if (error instanceof ApiError) {
    return classifyMemedApiFailure({
      status: Number(error.status),
      code: extractCode(error.response),
      message: error.message,
      missingFields: extractMissingFields(error.response),
    });
  }

  if (error && typeof error === 'object') {
    const obj = error as {
      status?: number;
      code?: string;
      error?: string;
      message?: string;
      missingFields?: string[];
      response?: unknown;
    };
    return classifyMemedApiFailure({
      status: obj.status,
      code: obj.code || extractCode(obj.response) || extractCode(obj),
      message: obj.error || obj.message,
      missingFields:
        (Array.isArray(obj.missingFields) ? obj.missingFields : undefined) ||
        extractMissingFields(obj.response) ||
        extractMissingFields(obj),
    });
  }

  return classifyMemedApiFailure({
    message: error instanceof Error ? error.message : undefined,
  });
}

/**
 * Obtém sessão/token efêmero via backend (T7).
 * Token permanece apenas no retorno em memória.
 */
export async function getMemedSession(): Promise<MemedSession> {
  let res: ApiResponse<MemedSession>;
  try {
    res = await apiClient.get<MemedSession>(SESSION_PATH);
  } catch (error) {
    throw toMemedError(error);
  }

  if (!res.success || !res.data?.token || typeof res.data.token !== 'string') {
    throw toMemedError({
      status: res.status,
      code: res.code || extractCode(res),
      message: res.error || res.message || 'Sessão Memed inválida',
      response: res,
      missingFields: extractMissingFields(res),
    });
  }

  const session: MemedSession = { token: res.data.token.trim() };
  if (res.data.expiresAt) session.expiresAt = res.data.expiresAt;
  if (typeof res.data.expiresIn === 'number') session.expiresIn = res.data.expiresIn;
  return session;
}

/** Status público (feature). 503/disabled → erro classificado. */
export async function getMemedStatus(): Promise<MemedStatus> {
  let res: ApiResponse<MemedStatus>;
  try {
    res = await apiClient.get<MemedStatus>(STATUS_PATH);
  } catch (error) {
    throw toMemedError(error);
  }

  if (!res.success || !res.data) {
    throw toMemedError({
      status: res.status,
      code: res.code,
      message: res.error || res.message,
      response: res,
    });
  }

  return res.data;
}
